import type { GameInput, GameKeyboard, InputRouterOptions, KeyboardHolds, KeyboardOwner, LayerHandle, LayerKeyAnswer, LayerOptions, RoutedKey } from "./router-types.ts";

/**
 * The input router (ADR-061 decision 4): the one place that decides who owns the keyboard and what Escape
 * answers. While a DOM text field has focus the field owns every key and the game's keyboard is switched
 * off, so nothing it captures is prevented and every character types; when the field loses focus the game
 * takes the keyboard back at once. Each change of owner releases every key the game holds, so a movement
 * key held as a field takes focus never keeps the character walking. Open DOM layers form a stack: Escape
 * closes the topmost, and with none open the game may take it (`onEscape`: a held target clears), or else the
 * router reports that the game menu was asked for. A layer may
 * name other keys that close it, and keys it answers without closing; a key a layer closes on or answers is consumed,
 * so it reaches neither the game nor Phaser, and neither do its auto-repeats until it goes up. A layer's answer may
 * decline a press it is shown (one with Shift held, or one a focused control keeps for itself), and a declined press
 * goes on to the game as though the layer had not named the key. A fresh press is always answered, even when
 * the release before it was never seen (the window lost focus while the key was down). A layer takes no other key:
 * under a surface that is not modal (the flag panel, the reading panel) the ability keys, movement, R and Tab stay the
 * game's, and only a focused text field or a modal's hold keeps them from it (ADR-061's amendment of 2026-10-03).
 *
 * Keys are shortcuts, never the only way (ADR-061, "every action is reachable without a keyboard"):
 * `dismiss` is what Escape does, for a close button, a tap or a menu button, and a layer's handle closes
 * it from its own control.
 *
 * The router reads events off one `EventTarget` and never touches the DOM itself: what counts as a text
 * field and what has focus are handed to it, so its state is tested against Bun's `EventTarget`. On the
 * page the target is the window, listened to in the bubble phase and attached before Phaser's keyboard, so
 * a key the router consumes is already prevented when Phaser, which ignores prevented keys, sees it, and a
 * key a page control stopped (a native modal dialog's own Escape) never reaches the router.
 *
 * @example
 * ```ts
 * const router = new InputRouter({ target: window, isTextField, focused: () => document.activeElement });
 * router.onMenu(() => menu.show());
 * const panel = router.open({ onClose: () => (element.hidden = true), closeKeys: ["e"] });
 * closeButton.onclick = () => panel.close();
 * ```
 */

export type { GameInput, GameKeyboard, InputRouterOptions, KeyboardHolds, KeyboardOwner, LayerHandle, LayerKeyAnswer, LayerOptions, RoutedKey } from "./router-types.ts";

const ESCAPE = "Escape";

/**
 * A key's name for matching: a single character in lower case, so `E` and `e` are one key, and any other
 * name as it is.
 *
 * @param key The `KeyboardEvent.key` value.
 * @returns The name.
 */
export function keyName(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key;
}

/**
 * One entry of the layer stack.
 */
interface Layer {
  readonly onClose: () => void;
  readonly closeKeys: ReadonlySet<string>;
  readonly keys: ReadonlyMap<string, LayerKeyAnswer>;
}

/**
 * The router.
 */
export class InputRouter implements GameInput, KeyboardHolds {
  private readonly layers: Layer[] = [];
  private readonly keyListeners = new Set<(event: RoutedKey) => void>();
  private readonly keyWatchers = new Set<(event: RoutedKey, down: boolean, owned: boolean) => void>();
  private readonly menuListeners = new Set<() => void>();
  private readonly escapeListeners = new Set<() => boolean>();
  private readonly layerListeners = new Set<() => void>();
  private readonly swallowed = new Set<string>();
  private keyboard: GameKeyboard | null = null;
  private fieldFocused: boolean;
  private holds = 0;
  private enabled = true;

  /**
   * Starts listening on the target.
   *
   * @param options The target, the text-field test and the focus reader.
   */
  public constructor(private readonly options: InputRouterOptions) {
    this.fieldFocused = options.isTextField(options.focused());
    this.enabled = this.gameOwns();
    options.target.addEventListener("keydown", this.onKeyDown);
    options.target.addEventListener("keyup", this.onKeyUp);
    options.target.addEventListener("focusin", this.onFocusIn);
    options.target.addEventListener("focusout", this.onFocusOut);
  }

  /**
   * Who owns the keyboard now.
   *
   * @returns The owner.
   */
  public get owner(): KeyboardOwner {
    return this.fieldFocused ? "field" : this.holds > 0 ? "hold" : "game";
  }

  /**
   * Whether the topmost layer names a key. An answer may still decline a press of it (one with Shift held), which
   * this does not ask. It is asked each frame by what the scene shows a key doing, so it makes nothing: the key is
   * taken as the key table holds it and is not put in lower case here.
   *
   * @param key The key by `keyName`, or a physical key by its code, as the key table holds it.
   * @returns True while a layer is open and the topmost closes on the key or answers it.
   */
  public takes(key: string): boolean {
    const top = this.layers.at(-1);
    return top !== undefined && (top.closeKeys.has(key) || top.keys.has(key));
  }

  /**
   * Listens for the stack of layers changing, a layer opened or closed, for a surface that shows whether its own
   * layer is the topmost (`LayerHandle.top`).
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public onLayers(listener: () => void): () => void {
    this.layerListeners.add(listener);
    return () => this.layerListeners.delete(listener);
  }

  /**
   * Tells the listeners that the stack of layers changed.
   */
  private tellLayers(): void {
    for (const listener of [...this.layerListeners]) {
      listener();
    }
  }

  /**
   * How many layers are open.
   *
   * @returns The depth of the stack.
   */
  public get depth(): number {
    return this.layers.length;
  }

  /**
   * Hands the game's keyboard to the router, which re-reads what has focus and sets the keyboard to the
   * current owner at once, releasing every key.
   *
   * @param keyboard The keyboard.
   * @returns A function that unbinds it, if it is still the bound one.
   */
  public bindKeyboard(keyboard: GameKeyboard): () => void {
    this.keyboard = keyboard;
    this.fieldFocused = this.options.isTextField(this.options.focused());
    this.enabled = this.gameOwns();
    keyboard.setEnabled(this.enabled);
    keyboard.release();
    return () => {
      if (this.keyboard === keyboard) {
        this.keyboard = null;
      }
    };
  }

  /**
   * Takes the keyboard from the game until released, whatever has focus.
   *
   * @returns A function that releases this hold; calling it again does nothing.
   */
  public hold(): () => void {
    this.holds += 1;
    this.settle();
    let held = true;
    return () => {
      if (held) {
        held = false;
        this.holds -= 1;
        this.settle();
      }
    };
  }

  /**
   * Opens a layer on top of the stack.
   *
   * @param options How the layer hides and which keys beside Escape close it.
   * @returns The layer's handle.
   */
  public open(options: LayerOptions): LayerHandle {
    const layer: Layer = { onClose: options.onClose, closeKeys: new Set((options.closeKeys ?? []).map(keyName)), keys: new Map(Object.entries(options.keys ?? {}).map(([key, answer]) => [keyName(key), answer])) };
    this.layers.push(layer);
    this.tellLayers();
    const layers = this.layers;
    return {
      /**
       * Whether the layer is still in the stack.
       *
       * @returns True while open.
       */
      get open(): boolean {
        return layers.includes(layer);
      },
      get top(): boolean {
        return layers.at(-1) === layer;
      },
      /**
       * Closes the layer from its own control, once.
       */
      close: () => this.close(layer),
    };
  }

  /**
   * What Escape does, for a close button, a tap or a menu button: closes the topmost layer, or with none
   * open reports that the game menu was asked for.
   *
   * @returns `closed` when a layer closed, `menu` when the menu was asked for.
   */
  public dismiss(): "closed" | "menu" {
    const top = this.layers.at(-1);
    if (top !== undefined) {
      this.close(top);
      return "closed";
    }
    for (const listener of this.menuListeners) {
      listener();
    }
    return "menu";
  }

  /**
   * Listens for the game menu being asked for.
   *
   * @param listener Called each time.
   * @returns A function that stops listening.
   */
  public onMenu(listener: () => void): () => void {
    this.menuListeners.add(listener);
    return () => this.menuListeners.delete(listener);
  }

  /**
   * Listens for Escape pressed with no layer open while the game owns the keyboard, before the menu is asked for.
   *
   * @param listener Called with each such press; true when it took it.
   * @returns A function that stops listening.
   */
  public onEscape(listener: () => boolean): () => void {
    this.escapeListeners.add(listener);
    return () => this.escapeListeners.delete(listener);
  }

  /**
   * Whether a listener for Escape takes a press: asked in the order they listened, the first to answer true wins.
   *
   * @returns True when one took it.
   */
  private escapeTaken(): boolean {
    for (const listener of this.escapeListeners) {
      if (listener()) {
        return true;
      }
    }
    return false;
  }

  /**
   * Listens for the keys the game owns.
   *
   * @param listener Called with each key press the router lets through.
   * @returns A function that stops listening.
   */
  public onKey(listener: (event: RoutedKey) => void): () => void {
    this.keyListeners.add(listener);
    return () => this.keyListeners.delete(listener);
  }

  /**
   * Watches every key going down or up.
   *
   * @param listener Called with each press and release, whether it went down and whether the game owned the keyboard.
   * @returns A function that stops watching.
   */
  public watchKeys(listener: (event: RoutedKey, down: boolean, owned: boolean) => void): () => void {
    this.keyWatchers.add(listener);
    return () => this.keyWatchers.delete(listener);
  }

  /**
   * Whether a key going down is a focused control's or the top layer's rather than the game's (`GameInput.takenElsewhere`).
   *
   * @param event The press.
   * @returns True when a control already took it or the top layer names it.
   */
  public takenElsewhere(event: RoutedKey): boolean {
    if (event.defaultPrevented) {
      return true;
    }
    const top = this.layers.at(-1);
    if (top === undefined || this.options.isTextField(event.target)) {
      return false;
    }
    const name = keyName(event.key);
    const code = event.code === undefined || event.code === "" ? null : event.code;
    return top.keys.has(name) || top.closeKeys.has(name) || (code !== null && (top.keys.has(code) || top.closeKeys.has(code)));
  }

  /**
   * Stops listening on the target and forgets every listener and layer.
   */
  public dispose(): void {
    const target = this.options.target;
    target.removeEventListener("keydown", this.onKeyDown);
    target.removeEventListener("keyup", this.onKeyUp);
    target.removeEventListener("focusin", this.onFocusIn);
    target.removeEventListener("focusout", this.onFocusOut);
    this.keyListeners.clear();
    this.keyWatchers.clear();
    this.menuListeners.clear();
    this.escapeListeners.clear();
    this.layerListeners.clear();
    this.layers.length = 0;
    this.keyboard = null;
  }

  /**
   * Whether the game owns the keyboard in the current state.
   *
   * @returns True when no field has focus and nothing holds the keyboard.
   */
  private gameOwns(): boolean {
    return !this.fieldFocused && this.holds === 0;
  }

  /**
   * Brings the game's keyboard in line with the owner, releasing every key whenever it changes.
   */
  private settle(): void {
    const enabled = this.gameOwns();
    if (enabled === this.enabled) {
      return;
    }
    this.enabled = enabled;
    this.keyboard?.setEnabled(enabled);
    this.keyboard?.release();
  }

  /**
   * Takes a layer out of the stack and hides it, once.
   *
   * @param layer The layer.
   */
  private close(layer: Layer): void {
    const index = this.layers.indexOf(layer);
    if (index === -1) {
      return;
    }
    this.layers.splice(index, 1);
    layer.onClose();
    this.tellLayers();
  }

  /**
   * Consumes a key and keeps consuming its auto-repeats until it goes up or is pressed afresh.
   *
   * @param event The key press.
   * @param name The key's name.
   */
  private consume(event: RoutedKey, name: string): void {
    event.preventDefault();
    this.swallowed.add(name);
  }

  /**
   * Answers a key press, shown first to the watchers: Escape (with no layer open, the game's own Escape listeners first, then the menu), then a
   * topmost layer's close keys and its own keys, then the game's keys while the
   * game owns the keyboard. A key's own target settles who owns the keyboard before anything else, so a
   * field that left without a `focusout` (removed from the page) cannot keep the keyboard from the game.
   * Only an auto-repeat of a consumed key is consumed again: a press that is not a repeat means the key
   * went up unseen, so it ends the consumption and is answered like any other.
   *
   * @param raw The event.
   */
  private readonly onKeyDown = (raw: Event): void => {
    const event = raw as unknown as RoutedKey;
    const inField = this.options.isTextField(event.target);
    if (!event.defaultPrevented && inField !== this.fieldFocused) {
      this.fieldFocused = inField;
      this.settle();
    }
    for (const watcher of this.keyWatchers) {
      watcher(event, true, !inField && this.gameOwns());
    }
    if (event.defaultPrevented) {
      return;
    }
    const name = keyName(event.key);
    if (this.swallowed.has(name)) {
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      this.swallowed.delete(name);
    }
    const top = this.layers.at(-1);
    if (name === ESCAPE) {
      if (event.repeat || (inField && top === undefined)) {
        return;
      }
      this.consume(event, name);
      if (top === undefined && !inField && this.gameOwns() && this.escapeTaken()) {
        return;
      }
      this.dismiss();
      return;
    }
    const code = event.code === undefined || event.code === "" ? null : event.code;
    if (top !== undefined && (top.closeKeys.has(name) || (code !== null && top.closeKeys.has(code))) && !inField && !event.repeat) {
      this.consume(event, name);
      this.close(top);
      return;
    }
    const answer = top === undefined || inField || event.repeat ? undefined : (top.keys.get(name) ?? (code === null ? undefined : top.keys.get(code)));
    if (answer !== undefined && answer(event) !== false) {
      this.consume(event, name);
      return;
    }
    if (!this.gameOwns()) {
      return;
    }
    for (const listener of this.keyListeners) {
      listener(event);
    }
  };

  /**
   * Ends the consumption of a key that went up, and shows the release to the watchers.
   *
   * @param raw The event.
   */
  private readonly onKeyUp = (raw: Event): void => {
    const event = raw as unknown as RoutedKey;
    this.swallowed.delete(keyName(event.key));
    for (const watcher of this.keyWatchers) {
      watcher(event, false, !this.options.isTextField(event.target) && this.gameOwns());
    }
  };

  /**
   * Gives the keyboard to a text field that took focus, or to the game when anything else did.
   *
   * @param event The event.
   */
  private readonly onFocusIn = (event: Event): void => {
    this.fieldFocused = this.options.isTextField(event.target);
    this.settle();
  };

  /**
   * Gives the keyboard back to the game when focus leaves a field for anything but another field.
   *
   * @param event The event, whose `relatedTarget` is what takes focus next.
   */
  private readonly onFocusOut = (event: Event): void => {
    this.fieldFocused = this.options.isTextField((event as Event & { readonly relatedTarget?: unknown }).relatedTarget ?? null);
    this.settle();
  };
}
